import { GripVerticalIcon } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

export interface ResizeHandleProps {
  onResize: (deltaX: number) => void
  onResizeStart?: () => void
  onResizeEnd?: () => void
  className?: string
}

export const ResizeHandle: React.FC<ResizeHandleProps> = ({
  onResize,
  onResizeStart,
  onResizeEnd,
  className,
}): ReactNode => {
  const [isDragging, setIsDragging] = useState(false)
  const startXRef = useRef(0)

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      setIsDragging(true)
      startXRef.current = e.clientX
      onResizeStart?.()
    },
    [onResizeStart]
  )

  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent): void => {
      const deltaX = e.clientX - startXRef.current
      startXRef.current = e.clientX
      onResize(deltaX)
    }

    const handleMouseUp = (): void => {
      setIsDragging(false)
      onResizeEnd?.()
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, onResize, onResizeEnd])

  return (
    <button
      type="button"
      aria-label="Resize panels"
      className={cn(
        'resize-handle group relative flex w-2 cursor-col-resize items-center justify-center border-0 bg-border p-0 transition-colors hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isDragging && 'bg-primary/30',
        className
      )}
      onMouseDown={handleMouseDown}
    >
      <span
        className={cn(
          'resize-handle-grip flex h-8 w-4 items-center justify-center rounded-sm border bg-border opacity-0 transition-opacity group-hover:opacity-100',
          isDragging && 'opacity-100'
        )}
      >
        <GripVerticalIcon className="size-3 text-muted-foreground" />
      </span>
    </button>
  )
}
